// Loại view dùng chung cho UI SCR-050: hai domain (organization/administrative area) có hình dạng
// giống nhau, chỉ khác nhãn phân loại (`type` vs `level`). Section adapter map DTO gốc sang các type này.
// Implementation decision (Spec S2.2.5 §12.1): một workspace generic dùng chung thay vì copy-paste.

export type AdminNode = Readonly<{
  id: string;
  code: string;
  name: string;
  /** Giá trị phân loại: `type` của organization hoặc `level` của administrative area. */
  classification: string;
  parentId?: string | null | undefined;
  status: string;
}>;

export type AdminDetail = AdminNode &
  Readonly<{
    /** Format: date — ngày bắt đầu hiệu lực của lát cắt as-of. */
    validFrom: string;
    /** Format: date | null — null nghĩa là đang mở (∞). */
    validTo?: string | null | undefined;
    /** versionNo nghiệp vụ của lát cắt as-of — dùng làm expectedVersion. */
    version: number;
  }>;

export type AdminVersion = Readonly<{
  version: number;
  name: string;
  classification: string;
  parentId?: string | null | undefined;
  status: string;
  validFrom: string;
  validTo?: string | null | undefined;
}>;

export type AdminCommandOutcome = Readonly<{
  id: string;
  version: number;
  rehomedChildIds?: string[] | undefined;
}>;

export type AdminCreateInput = Readonly<{
  code: string;
  name: string;
  classification: string;
  parentId?: string | null | undefined;
  validFrom: string;
  reason: string;
}>;

export type AdminRenameInput = Readonly<{
  id: string;
  expectedVersion: number;
  newName: string;
  effectiveFrom: string;
  decisionReference: string;
  reason?: string | undefined;
}>;

export type AdminTransferInput = Readonly<{
  id: string;
  expectedVersion: number;
  newParentId: string | null;
  effectiveFrom: string;
  decisionReference: string;
  reason?: string | undefined;
}>;

export type AdminMergeInput = Readonly<{
  survivorId: string;
  survivorExpectedVersion: number;
  absorbedId: string;
  absorbedExpectedVersion: number;
  effectiveFrom: string;
  decisionReference: string;
  reason?: string | undefined;
}>;

export type AdminCloseInput = Readonly<{
  id: string;
  expectedVersion: number;
  effectiveFrom: string;
  decisionReference: string;
  reason?: string | undefined;
}>;

export type AdminOpenInput = Readonly<{
  id: string;
  expectedVersion: number;
  effectiveFrom: string;
  reason: string;
}>;

/** API mặt tiền mà mỗi section (organization / administrative area) cung cấp cho AdminWorkspace. */
export type AdminWorkspaceApi = Readonly<{
  getTree(signal?: AbortSignal): Promise<AdminNode[]>;
  getDetail(id: string, signal?: AbortSignal): Promise<AdminDetail>;
  getHistory(id: string, signal?: AbortSignal): Promise<AdminVersion[]>;
  create(input: AdminCreateInput, idempotencyKey: string): Promise<AdminCommandOutcome>;
  rename(input: AdminRenameInput, idempotencyKey: string): Promise<AdminCommandOutcome>;
  transfer(input: AdminTransferInput, idempotencyKey: string): Promise<AdminCommandOutcome>;
  merge(input: AdminMergeInput, idempotencyKey: string): Promise<AdminCommandOutcome>;
  close(input: AdminCloseInput, idempotencyKey: string): Promise<AdminCommandOutcome>;
  open(input: AdminOpenInput, idempotencyKey: string): Promise<AdminCommandOutcome>;
}>;

export const CLASSIFICATION_NAMES: Readonly<Record<string, string>> = {
  PROVINCIAL: "Cấp tỉnh",
  DISTRICT: "Cấp huyện",
  COMMUNE: "Cấp xã",
  TINH: "Tỉnh/Thành phố",
  HUYEN: "Huyện/Quận",
  XA: "Xã/Phường",
};

export function formatClassification(value: string): string {
  if (!value) return "—";
  return CLASSIFICATION_NAMES[value.trim().toUpperCase()] ?? value;
}

/** Thu thập id của node và toàn bộ hậu duệ (duyệt Map con O(n) một lượt, không `.find()` lặp). */
export function collectSelfAndDescendantIds(
  childrenByParent: ReadonlyMap<string, AdminNode[]>,
  rootId: string,
): Set<string> {
  const result = new Set<string>([rootId]);
  const stack = [rootId];
  while (stack.length > 0) {
    const current = stack.pop() as string;
    for (const child of childrenByParent.get(current) ?? []) {
      if (!result.has(child.id)) {
        result.add(child.id);
        stack.push(child.id);
      }
    }
  }
  return result;
}
